import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// eslint-disable-next-line import/no-unresolved
import type { Content } from '@plone/types';
import { buildAnalyticsPayload, pushAnalyticsPayload } from './analytics';

let lastAutomaticPush:
  | {
      dataLayer: unknown;
      signature?: string;
    }
  | undefined;

interface DataLayerAnalyticsProps {
  content: Content;
  pathname?: string;
  search?: string;
}

const hasPushedAutomaticPayload = (signature: string) => {
  if (typeof window === 'undefined') {
    return false;
  }

  const analyticsWindow = window as typeof window & {
    dataLayer?: unknown[];
  };
  analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];

  if (lastAutomaticPush?.dataLayer !== analyticsWindow.dataLayer) {
    lastAutomaticPush = {
      dataLayer: analyticsWindow.dataLayer,
    };
  }

  if (lastAutomaticPush.signature === signature) {
    return true;
  }

  lastAutomaticPush.signature = signature;
  return false;
};

export const DataLayerAnalytics = ({
  content,
  pathname,
  search,
}: DataLayerAnalyticsProps) => {
  const location = useLocation();
  const currentPathname = pathname || location.pathname;
  const currentSearch = search ?? location.search;

  useEffect(() => {
    const payload = buildAnalyticsPayload(content, {
      pathname: currentPathname,
      search: currentSearch,
    });
    const payloadSignature = payload ? JSON.stringify(payload) : undefined;

    if (!payloadSignature || hasPushedAutomaticPayload(payloadSignature)) {
      return;
    }

    pushAnalyticsPayload(payload);
  }, [content, currentPathname, currentSearch]);

  return null;
};
